import { animate, motion, useMotionValue, useTransform } from 'framer-motion';
import { useEffect, useState } from 'react';

import { cn } from '@/utils/cn';

interface PingCounterProps {
  startPing?: number;
  endPing?: number;
  duration?: number;
  className?: string;
  accentColor?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showLabel?: boolean;
  autoPlay?: boolean;
  labelOptimizing?: string;
  labelDone?: string;
}

export function PingCounter({ startPing = 180, endPing = 12, duration = 2.5, className, accentColor = '#93FF18', size = 'lg', showLabel = true, autoPlay = true, labelOptimizing = 'Optimizing...', labelDone = 'Your ping with NoPing' }: PingCounterProps) {
  const count = useMotionValue(startPing);
  const rounded = useTransform(count, (v) => Math.round(v));
  const [displayValue, setDisplayValue] = useState(startPing);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const unsubscribe = rounded.on('change', (v) => setDisplayValue(v));
    return unsubscribe;
  }, [rounded]);

  useEffect(() => {
    if (!autoPlay) return undefined;
    const controls = animate(count, endPing, {
      duration,
      ease: 'easeOut',
      onComplete: () => setHasAnimated(true),
    });
    return controls.stop;
  }, [count, endPing, duration, autoPlay]);

  const sizeClasses = {
    sm: 'text-3xl',
    md: 'text-5xl',
    lg: 'text-7xl',
    xl: 'text-9xl',
  };

  const highPingColor = displayValue > 50 ? '#FFB800' : accentColor;
  const pingColor = displayValue > 100 ? '#FF4444' : highPingColor;

  return (
    <div className={cn('flex flex-col items-center gap-1', className)}>
      <motion.div className={cn('font-mono font-black tabular-nums', sizeClasses[size])} style={{ color: pingColor }} animate={hasAnimated ? { scale: [1, 1.05, 1] } : undefined} transition={{ duration: 0.3 }}>
        {displayValue}
        <span className='text-[0.4em] font-normal opacity-60'>ms</span>
      </motion.div>
      {showLabel && <span className='text-xs uppercase tracking-[0.2em] text-[#929298]'>{hasAnimated ? labelDone : labelOptimizing}</span>}
    </div>
  );
}
